import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../icons/icon/icon.component';

@Component({
  selector: 'ui-inline-text-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent],
  templateUrl: './inline-text-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineTextEditComponent {
  // Using model for two-way binding
  readonly value = model('');
  readonly placeholder = input('');
  readonly disabled = input(false);

  // Output for change event (when editing is complete)
  readonly change = output<string>();

  // View children using signal-based API
  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  // Track editing state
  readonly isEditing = signal(false);
  private originalValue = '';

  constructor() {
    // Focus input when entering edit mode
    effect(() => {
      if (this.isEditing()) {
        requestAnimationFrame(() => {
          const input = this.inputElement();
          if (input) {
            input.nativeElement.focus();
            input.nativeElement.select();
          }
        });
      }
    });
  }

  onValueChange(newValue: string) {
    this.value.set(newValue);
  }

  onBlur() {
    this.isEditing.set(false);
    if (this.value() !== this.originalValue) {
      this.change.emit(this.value());
    }
  }

  onEscape() {
    this.value.set(this.originalValue);
    this.isEditing.set(false);
  }

  focusInput() {
    if (!this.disabled()) {
      this.originalValue = this.value();
      this.isEditing.set(true);
    }
  }
}
